import {
    Box,
    Button,
    Chip,
    Divider,
    Link,
    Paper,
    Stack,
    Typography,
} from "@mui/material"
import { LocalPhone, ShieldOutlined } from "@mui/icons-material"

import { useEnquire } from "../../../components/EnquireModal"
import { PHONE_DISPLAY, PHONE_HREF } from "../../../constants/contactInfo"
import { pillButtonSx } from "../../../styles/sectionStyles"

const PackageRow = ({ title, weekly, deposit }) => (
    <Box
        sx={{
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: "rgba(0, 4, 53, 0.03)",
            border: "1px solid rgba(0, 4, 53, 0.08)",
        }}
    >
        <Typography variant="subtitle2" fontWeight={800} color="#000435">
            {title}
        </Typography>
        <Typography variant="h5" fontWeight={800} color="#000435" sx={{ mt: 0.5 }}>
            £{Number(weekly).toFixed(0)}
            <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={{ ml: 0.75 }}
            >
                per week
            </Typography>
        </Typography>
        {deposit != null && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Deposit £{Number(deposit).toFixed(0)}
            </Typography>
        )}
    </Box>
)

const HirePanel = ({ vehicle, compact = false }) => {
    const openEnquire = useEnquire()
    const isAvailable = vehicle.available === 1
    const packages = vehicle.hirePackages || {}
    const withPkg = packages.withInsurance
    const withoutPkg = packages.withoutInsurance
    const dual = Boolean(withPkg && withoutPkg)
    const single = withPkg || withoutPkg

    return (
        <Paper
            elevation={0}
            sx={{
                p: compact ? 2.5 : 3,
                borderRadius: 2,
                border: "1px solid rgba(0, 4, 53, 0.1)",
                bgcolor: "#fff",
                position: "relative",
            }}
        >
            {!dual && single && (
                <Typography variant="h3" fontWeight={800} color="#000435" lineHeight={1}>
                    £{Number(single.weekly).toFixed(0)}
                    <Typography
                        component="span"
                        variant="body1"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ ml: 0.75 }}
                    >
                        per week
                    </Typography>
                </Typography>
            )}

            {dual && (
                <Typography variant="h6" fontWeight={800} color="#000435" sx={{ mb: 1.5 }}>
                    Hire packages
                </Typography>
            )}

            <Chip
                label={vehicle.availabilityLabel}
                size="small"
                sx={{
                    mt: dual ? 0 : 1.5,
                    mb: 2,
                    fontWeight: 700,
                    ...(isAvailable
                        ? {
                              bgcolor: "rgba(0, 182, 122, 0.12)",
                              color: "#007A52",
                          }
                        : vehicle.available === 0
                          ? {
                                bgcolor: "rgba(232, 149, 0, 0.14)",
                                color: "#9A5B00",
                            }
                          : {
                                bgcolor: "rgba(0, 4, 53, 0.08)",
                                color: "#000435",
                            }),
                }}
            />

            {dual && (
                <Stack spacing={1.25} sx={{ mb: 2 }}>
                    <PackageRow
                        title="With insurance"
                        weekly={withPkg.weekly}
                        deposit={withPkg.deposit}
                    />
                    <PackageRow
                        title="Without insurance"
                        weekly={withoutPkg.weekly}
                        deposit={withoutPkg.deposit}
                    />
                </Stack>
            )}

            <Stack spacing={1.25} sx={{ mb: 2.5 }}>
                <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    color="secondary"
                    sx={pillButtonSx}
                    onClick={() => openEnquire?.({ carName: vehicle.name })}
                >
                    Enquire about this vehicle
                </Button>
                <Button
                    fullWidth
                    size="large"
                    variant="outlined"
                    color="primary"
                    component={Link}
                    href={PHONE_HREF}
                    startIcon={<LocalPhone />}
                    sx={pillButtonSx}
                >
                    Call {PHONE_DISPLAY}
                </Button>
            </Stack>

            {!dual && withPkg && (
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
                    <ShieldOutlined sx={{ fontSize: 20, color: "#00B67A", mt: 0.15 }} />
                    <Typography variant="body2" fontWeight={600} lineHeight={1.5}>
                        Fully comprehensive insurance included
                    </Typography>
                </Stack>
            )}

            {!dual && withoutPkg && (
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
                    <ShieldOutlined sx={{ fontSize: 20, color: "text.secondary", mt: 0.15 }} />
                    <Typography variant="body2" fontWeight={600} lineHeight={1.5}>
                        Insurance arranged separately — speak to our team
                    </Typography>
                </Stack>
            )}

            {dual && (
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
                    <ShieldOutlined sx={{ fontSize: 20, color: "#00B67A", mt: 0.15 }} />
                    <Typography variant="body2" fontWeight={600} lineHeight={1.5}>
                        Choose with or without fully comprehensive insurance
                    </Typography>
                </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={800} color="#000435" gutterBottom>
                Weekly payments include
            </Typography>
            <Stack spacing={0.75} sx={{ mb: 2 }}>
                {vehicle.includes.slice(0, 5).map((item) => (
                    <Typography key={item} variant="body2" color="text.secondary">
                        · {item}
                    </Typography>
                ))}
            </Stack>

            <Stack spacing={1}>
                {[
                    !dual && single?.deposit != null
                        ? ["Deposit", `£${single.deposit}`]
                        : null,
                    [
                        "Minimum term",
                        `${vehicle.hire.minWeeks} week${vehicle.hire.minWeeks === 1 ? "" : "s"}`,
                    ],
                    ["Collection", "Blaydon office"],
                ]
                    .filter(Boolean)
                    .map(([label, value]) => (
                        <Stack
                            key={label}
                            direction="row"
                            justifyContent="space-between"
                            spacing={2}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {label}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {value}
                            </Typography>
                        </Stack>
                    ))}
            </Stack>
        </Paper>
    )
}

export default HirePanel
